import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, orderBy, query, doc, getDoc } from 'firebase/firestore';
import { updateDocument, getDocument } from './firestore';
import { db } from './firebase';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { 
  FaTrophy, 
  FaGamepad, 
  FaStar, 
  FaFire, 
  FaBookOpen, 
  FaSpinner,
  FaRocket, 
  FaEye, 
  FaLightbulb, 
  FaCog, 
  FaBook,
  FaUniversalAccess, 
  FaListAlt
} from 'react-icons/fa';

// Add this categoryIcons map before the Stats component
const categoryIcons = {
  "Operable": <FaRocket className="text-[var(--interactive-color)] text-2xl" />,
  "Perceivable": <FaEye className="text-[var(--interactive-color)] text-2xl" />,
  "Understandable": <FaLightbulb className="text-[var(--interactive-color)] text-2xl" />,
  "Robust": <FaCog className="text-[var(--interactive-color)] text-2xl" />,
  "Introduction": <FaBook className="text-[var(--interactive-color)] text-2xl" />,
  "WCAG": <FaUniversalAccess className="text-[var(--interactive-color)] text-2xl" />,
  "Cumulative": <FaListAlt className="text-[var(--interactive-color)] text-2xl" />
};

const Stats = ({ isLightMode }) => {
  const auth = getAuth();
  const db = getFirestore();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleUsers, setVisibleUsers] = useState(3);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('Anonymous');
  const [profilePic, setProfilePic] = useState('profilepicture.png');
  const [gameplayData, setGameplayData] = useState(null);
  const [allGameplayData, setAllGameplayData] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [rankingData, setRankingData] = useState(null);
  const [categoryStats, setCategoryStats] = useState([]);
  const [streakData, setStreakData] = useState({ streak: 0 });
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setIsLoggedIn(true);
        setProfilePic(user.photoURL || 'profilepicture.png');

        try {
          // Fetch user data from Firestore
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setUserName(`${userData.firstName} ${userData.lastName}`);
          } else {
            console.log('No such document!');
          }
          
          // Fetch all required data with proper error handling
          await Promise.all([
            fetchLeaderboardData(user.uid),
            fetchCategoryStats(user.uid),
            fetchStreakData(user.uid),
            fetchUserGameplayData(user.uid),
            fetchAllGameplayData(user.uid)
          ]);
          
        } catch (err) {
          console.error("Error loading user data:", err);
          setError("Failed to load some user data. Please try again later.");
        }
      } else {
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth, db, navigate]);

  // Fetch user gameplay data
  const fetchUserGameplayData = async (userId) => {
    try {
      const subCollectionPath = 'gameplay';
      const data = await getDocument(subCollectionPath, userId);
      
      // If data doesn't exist, set default values instead of throwing error
      setGameplayData(data || { 
        level: 1, 
        score: 0, 
        games_played: 0
      });
    } catch (err) {
      console.error("Error fetching gameplay data:", err);
      // Set default values on error
      setGameplayData({ 
        level: 1, 
        score: 0, 
        games_played: 0
      });
    }
  };

  // Fetch all gameplay data
  const fetchAllGameplayData = async (userId) => {
    try {
      const gameplayCollectionRef = collection(db, 'gameplay', userId, 'gameplays');
      const querySnapshot = await getDocs(gameplayCollectionRef);
      
      if (!querySnapshot.empty) {
        const data = querySnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        })).sort((a, b) => new Date(b.played_at) - new Date(a.played_at));
        
        setAllGameplayData(data);
      }
    } catch (err) {
      console.error("Error fetching all gameplay data:", err);
      setAllGameplayData([]);
    }
  };

  // Get user streak with error handling
  const getUserStreak = async (userId) => {
    try {
      const userDocPath = `users`;
      const streakDoc = await getDocument(userDocPath, userId);
      
      // Check if document exists and handle null case
      if (!streakDoc || !streakDoc.exists) {
        console.warn("User streak document not found, using default values");
        return { streak: 0 };
      }
      
      const currentStreak = streakDoc.streak || 0;
      return { streak: currentStreak };
    } catch (err) {
      console.error("Error fetching user streak:", err);
      return { streak: 0 };
    }
  };

  // Fetch streak data with error handling
  const fetchStreakData = async (userId) => {
    try {
      const data = await getUserStreak(userId);
      setStreakData(data);
    } catch (err) {
      console.error("Error in fetchStreakData:", err);
      setStreakData({ streak: 0 });
    }
  };

  // Fetch leaderboard data with error handling
  const fetchLeaderboardData = async (userId) => {
    try {
      const gameplayCollectionRef = collection(db, 'gameplay');
      const gameplayQuery = query(gameplayCollectionRef, orderBy('score', 'desc'));
      const gameplaySnapshot = await getDocs(gameplayQuery);

      const leaderboardData = gameplaySnapshot.docs.map((doc, index) => {
        const data = doc.data();
        return {
          rank: index + 1,
          id: doc.id,
          firstName: data.firstName || 'Unknown',
          lastName: data.lastName || 'User',
          score: data.score || 0,
        };
      });

      // Set the leaderboard data
      setUsers(leaderboardData);

      // Find the logged-in user's rank
      const user = leaderboardData.find((user) => user.id === userId);
      if (user) {
        setUserRank(user.rank);
      } else {
        // If user not found in leaderboard, assign a default rank
        setUserRank('N/A');
      }
      
      // Calculate ranking data safely
      let ranking = 0;
      let userScore = 0;
      let userFound = false;

      gameplaySnapshot.forEach((doc) => {
        const data = doc.data();
        ranking++;

        if (doc.id === userId) {
          userScore = data?.score || 0;
          userFound = true;
        }
      });

      setRankingData({
        ranking: userFound ? ranking : 'N/A',
        score: userScore
      });
      
    } catch (error) {
      console.error('Error fetching leaderboard data:', error);
      setUserRank('N/A');
      setRankingData({ ranking: 'N/A', score: 0 });
    }
  };

  // Fetch category stats with error handling
  const fetchCategoryStats = async (userId) => {
    try {
      const categories = ["Operable", "Cumulative", "Perceivable", "Understandable", "Introduction", "WCAG", "Robust"];
      const stats = [];
  
      for (const category of categories) {
        try {
          const categoryDocRef = doc(db, `questionstat/${userId}/${category}/stats`);
          const categoryDocSnap = await getDoc(categoryDocRef);
  
          if (categoryDocSnap.exists()) {
            const data = categoryDocSnap.data();
  
            // Calculate accuracy
            const totalQuestions = data.totalQuestions || 0;
            const correctAnswers = data.correctAnswers || 0;
            const wrongAnswers = data.wrongAnswers || 0;
            const accuracy = totalQuestions > 0 ? ((correctAnswers / totalQuestions) * 100).toFixed(2) : 0;
  
            stats.push({
              category,
              totalQuestions,
              correctAnswers,
              wrongAnswers,
              accuracy: parseFloat(accuracy), // Store as number for chart
              accuracyDisplay: `${accuracy}%`, // Format as percentage for display
            });
          } else {
            // Add default data for missing categories
            stats.push({
              category,
              totalQuestions: 0,
              correctAnswers: 0,
              wrongAnswers: 0,
              accuracy: 0,
              accuracyDisplay: '0%',
            });
          }
        } catch (categoryError) {
          console.error(`Error fetching data for category ${category}:`, categoryError);
          // Add default data for errored categories
          stats.push({
            category,
            totalQuestions: 0,
            correctAnswers: 0,
            wrongAnswers: 0,
            accuracy: 0,
            accuracyDisplay: '0%',
          });
        }
      }
  
      setCategoryStats(stats);
    } catch (error) {
      console.error("Error fetching category stats:", error);
      setCategoryStats([]);
    }
  };

  // Custom Loading component
  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--background-color)] flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="text-[var(--interactive-color)] text-5xl mx-auto animate-spin mb-4" />
          <p className="text-[var(--text-color)] text-xl font-medium">Loading your stats...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return null;
  }
  
  return (
    <div className="min-h-screen bg-[var(--background-color)] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Error message if any */}
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-md">
            <p>{error}</p>
          </div>
        )}
        
        {/* Profile and Stats Section */}
        <div className="bg-[var(--surface-color)] rounded-2xl shadow-xl p-6 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Profile Section */}
            <div className="lg:col-span-1">
              <div className="text-center">
                <div className="relative inline-block">
                  <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 border-[var(--interactive-color)] shadow-lg transform transition-transform duration-300 hover:scale-105">
                    <img
                      src={profilePic}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      onError={(e) => {e.target.src = '/profilepicture.png'}}
                    />
                  </div>
                  {/* Level Badge */}
                  <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-[var(--interactive-color)] rounded-full border-4 border-[var(--surface-color)] flex items-center justify-center">
                    <span className="text-white text-sm font-bold">
                      lvl {gameplayData?.level || 1}
                    </span>
                  </div>
                </div>
                <h2 className="mt-4 text-2xl font-bold text-[var(--primary-color)]">{userName}</h2>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Global Ranking */}
              <div className="bg-[var(--surface-color-hover)] rounded-xl p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-lg">
                <div className="flex flex-col items-center">
                  <div className="p-3 bg-[var(--interactive-color)] bg-opacity-20 rounded-full mb-3 flex items-center justify-center">
                    <FaTrophy className="text-[var(--interactive-color)] text-2xl" />
                  </div>
                  <p className="text-base font-medium text-[var(--text-color)] opacity-80">Global Ranking</p>
                  <h4 className="text-2xl font-bold text-[var(--primary-color)]">
                    #{userRank || 'N/A'}
                  </h4>
                </div>
              </div>

              {/* Games Played */}
              <div className="bg-[var(--surface-color-hover)] rounded-xl p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-lg">
                <div className="flex flex-col items-center">
                  <div className="p-3 bg-[var(--success-color)] bg-opacity-20 rounded-full mb-3 flex items-center justify-center">
                    <FaGamepad className="text-[var(--success-color)] text-2xl" />
                  </div>
                  <p className="text-base font-medium text-[var(--text-color)] opacity-80">Games Played</p>
                  <h4 className="text-2xl font-bold text-[var(--primary-color)]">
                    {gameplayData?.games_played || '0'}
                  </h4>
                </div>
              </div>

              {/* Total Score */}
              <div className="bg-[var(--surface-color-hover)] rounded-xl p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-lg">
                <div className="flex flex-col items-center">
                  <div className="p-3 bg-[var(--accent-color)] bg-opacity-20 rounded-full mb-3 flex items-center justify-center">
                    <FaStar className="text-[var(--accent-color)] text-2xl" />
                  </div>
                  <p className="text-base font-medium text-[var(--text-color)] opacity-80">Total Score</p>
                  <h4 className="text-2xl font-bold text-[var(--primary-color)]">
                    {gameplayData?.score?.toLocaleString() || '0'}
                  </h4>
                </div>
              </div>

              {/* Daily Streak */}
              <div className="bg-[var(--surface-color-hover)] rounded-xl p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-lg">
                <div className="flex flex-col items-center">
                  <div className="p-3 bg-[var(--primary-color)] bg-opacity-20 rounded-full mb-3 flex items-center justify-center">
                    <FaFire className="text-[var(--primary-color)] text-2xl" />
                  </div>
                  <p className="text-base font-medium text-[var(--text-color)] opacity-80">Daily Streak</p>
                  <h4 className="text-2xl font-bold text-[var(--primary-color)]">
                    {streakData?.streak || '0'}
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Category Stats Section */}
        <h2 className="text-2xl font-bold text-[var(--primary-color)] mb-6 text-center">Category Statistics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {categoryStats.length > 0 ? (
            categoryStats.map((stats, index) => (
              <div 
                key={index}
                className="bg-[var(--surface-color)] rounded-xl p-6 shadow-lg transform transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-[var(--interactive-color)] bg-opacity-20 rounded-full mr-4 flex items-center justify-center">
                    {categoryIcons[stats.category] || <FaBookOpen className="text-[var(--interactive-color)] text-2xl" />}
                  </div>
                  <h3 className="text-lg font-semibold text-[var(--primary-color)]">{stats.category}</h3>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-base mb-1">
                      <span className="text-[var(--text-color)] font-medium opacity-80">Accuracy</span>
                      <span className="text-[var(--interactive-color)] font-bold">{stats.accuracyDisplay}</span>
                    </div>
                    <div className="h-3 bg-[var(--border-color)] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[var(--interactive-color)] rounded-full transition-all duration-300"
                        style={{ width: `${stats.accuracy}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-base mt-4">
                    <div>
                      <p className="text-[var(--text-color)] font-medium opacity-80">Correct</p>
                      <p className="text-[var(--success-color)] font-bold text-lg">{stats.correctAnswers}</p>
                    </div>
                    <div>
                      <p className="text-[var(--text-color)] font-medium opacity-80">Total</p>
                      <p className="text-[var(--primary-color)] font-bold text-lg">{stats.totalQuestions}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-8">
              <p className="text-[var(--text-color)] text-lg">No category data available yet. Play more games to see your statistics!</p>
            </div>
          )}
        </div>

        {/* Performance Chart */}
        {categoryStats.length > 0 && (
          <div className="bg-[var(--surface-color)] rounded-xl p-6 shadow-xl">
            <h2 className="text-2xl font-bold text-[var(--primary-color)] mb-6 text-center">
              Performance Breakdown
            </h2>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={categoryStats}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--border-color)"
                    opacity={0.2}
                  />
                  <XAxis
                    dataKey="category"
                    stroke="var(--primary-color)"
                    tick={{ fill: "var(--primary-color)" }}
                    fontSize={12}
                    fontWeight={500}
                  />
                  <YAxis
                    stroke="var(--primary-color)"
                    tick={{ fill: "var(--primary-color)" }}
                    tickFormatter={(value) => value.toFixed(0)}
                    fontSize={12}
                    fontWeight={500}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "var(--surface-color)",
                      border: "none",
                      borderRadius: "0.5rem",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      color: "var(--text-color)",
                      fontWeight: 500
                    }}
                  />
                  <Legend 
                    wrapperStyle={{
                      fontSize: "14px",
                      fontWeight: 500
                    }}
                  />
                  <Bar
                    dataKey="correctAnswers"
                    name="Correct"
                    fill="var(--success-color)"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="wrongAnswers"
                    name="Wrong"
                    fill="var(--error-color)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Stats;